import { useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Tag,
  Button,
  Modal,
  Image,
  Space,
  Tabs,
  Divider,
} from "antd";
import {
  EnvironmentOutlined,
  CalendarOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const Portfolio = ({ projects: managedProjects = [] }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  const projects = managedProjects;
  const categories = [
    "All",
    ...new Set(projects.map((project) => project.category).filter(Boolean)),
  ];
  const filteredProjects =
    activeCategory === "All"
      ? projects
      : projects.filter((project) => project.category === activeCategory);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const retryImageLoad = (event) => {
    const image = event.currentTarget;
    if (image.dataset.retryAttempted) {
      return;
    }

    image.dataset.retryAttempted = "true";
    const retryUrl = new URL(image.currentSrc || image.src, window.location.href);
    retryUrl.searchParams.set("retry", Date.now().toString());

    window.setTimeout(() => {
      image.src = retryUrl.toString();
    }, 1000);
  };

  return (
    <section
      id="portfolio"
      className="portfolio-section"
      style={{ padding: "80px 50px", background: "#fff" }}
    >
      <div style={{ textAlign: "center", marginBottom: 50 }}>
        <Title level={2}>Our Portfolio</Title>
        <Paragraph style={{ maxWidth: 700, margin: "0 auto" }}>
          Discover our latest interior design and renovation projects.
        </Paragraph>
      </div>

      {!!projects.length && (
        <Tabs
          centered
          activeKey={activeCategory}
          onChange={setActiveCategory}
          items={categories.map((category) => ({
            key: category,
            label: category,
          }))}
        />
      )}

      <Row gutter={[32, 32]} style={{ marginTop: 40 }}>
        {filteredProjects.length ? (
          filteredProjects.map((project) => (
            <Col xs={24} md={12} lg={8} key={project.id}>
              <Card
                hoverable
                cover={
                  <Image
                    preview={false}
                    src={project.image_url}
                    loading="lazy"
                    onError={retryImageLoad}
                    height={260}
                    style={{ objectFit: "cover" }}
                  />
                }
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => openModal(project)}
                  >
                    View Details
                  </Button>,
                ]}
              >
                <Tag color="gold">{project.category}</Tag>
                <Title level={4} style={{ marginTop: 10 }}>
                  {project.title}
                </Title>
                <Paragraph ellipsis={{ rows: 2 }}>
                  {project.description}
                </Paragraph>
                <div className="project-meta">
                  <Space>
                    <EnvironmentOutlined />
                    <Text>{project.location}</Text>
                  </Space>
                  <Space>
                    <CalendarOutlined />
                    <Text>{project.date}</Text>
                  </Space>
                </div>
              </Card>
            </Col>
          ))
        ) : (
          <Col span={24}>
            <Text type="secondary">No portfolio projects available.</Text>
          </Col>
        )}
      </Row>

      <Modal
        open={isModalOpen}
        footer={null}
        width={1000}
        className="portfolio-modal"
        onCancel={() => setIsModalOpen(false)}
        centered
      >
        {selectedProject && (
          <div
            style={{
              maxHeight: "75vh",
              overflowY: "auto",
              paddingRight: 20,
              marginTop: 20,
            }}
          >
            <Image
              src={selectedProject.image_url}
              width="100%"
              loading="lazy"
              onError={retryImageLoad}
              height={350}
              style={{ objectFit: "cover", borderRadius: 12 }}
              preview={false}
            />

            <Title level={2} style={{ marginTop: 20 }}>
              {selectedProject.title}
            </Title>
            <Tag color="gold">{selectedProject.category}</Tag>
            <Paragraph style={{ marginTop: 15 }}>
              {selectedProject.description}
            </Paragraph>

            <Divider />

            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Title level={4}>Project Info</Title>
                <p>Client: {selectedProject.client}</p>
                <p>Duration: {selectedProject.duration}</p>
                <p>Size: {selectedProject.size}</p>
                <p>Budget: {selectedProject.budget}</p>
              </Col>
              <Col xs={24} sm={12}>
                <Title level={4}>Materials</Title>
                {(selectedProject.materials || []).map((material) => (
                  <p key={material}>{material}</p>
                ))}
              </Col>
            </Row>

            <Divider />

            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Title level={5}>Key Challenges</Title>
                {(selectedProject.challenges || []).map((challenge) => (
                  <p key={challenge}>{challenge}</p>
                ))}
              </Col>
              <Col xs={24} sm={12}>
                <Title level={5}>Solutions</Title>
                {(selectedProject.solutions || []).map((solution) => (
                  <p key={solution}>
                    <CheckCircleOutlined style={{ color: "green" }} />{" "}
                    {solution}
                  </p>
                ))}
              </Col>
            </Row>

            {selectedProject.before_image_url && (
              <>
                <Divider />
                <Title level={4}>Before & After</Title>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Image
                      src={selectedProject.before_image_url}
                      height={250}
                      loading="lazy"
                      onError={retryImageLoad}
                      style={{ objectFit: "cover", borderRadius: 10 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Image
                      src={selectedProject.image_url}
                      height={250}
                      loading="lazy"
                      onError={retryImageLoad}
                      style={{ objectFit: "cover", borderRadius: 10 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {!!selectedProject.gallery?.length && (
              <>
                <Divider />
                <Title level={4}>Gallery</Title>
                <Row gutter={[16, 16]}>
                  {selectedProject.gallery.map((imageUrl) => (
                    <Col xs={12} sm={8} key={imageUrl}>
                      <Image
                        src={imageUrl}
                        height={160}
                        loading="lazy"
                        onError={retryImageLoad}
                        style={{ objectFit: "cover", borderRadius: 8 }}
                      />
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

export default Portfolio;
